import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "../../api/client";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  AlertTriangle, ArrowRight, BadgeCheck, CalendarDays, CheckCircle2,
  Clock3, Download, FileCheck2, Landmark, Layers3, MessageSquareText,
  ShieldCheck, Sparkles,
} from "lucide-react";

const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
const dateBR = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("pt-BR"); } catch { return v; }
};

const brand = {
  ink: "#17211d",
  green: "#123c32",
  green2: "#17613f",
  accent: "#24a35a",
  soft: "#eaf5ee",
  muted: "#66736d",
  line: "#dce5e0",
  paper: "#ffffff",
  bg: "#f4f7f5",
};

function Section({ id, eyebrow, title, description, children, muted = false }) {
  return (
    <section id={id} className={`scroll-mt-36 rounded-2xl border p-5 md:p-8 ${muted ? "bg-[#f7faf8]" : "bg-white"}`} style={{ borderColor: brand.line }}>
      {eyebrow && <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#17613f] mb-2">{eyebrow}</div>}
      <h2 className="text-xl md:text-2xl font-semibold tracking-tight text-[#17211d]">{title}</h2>
      {description && <p className="mt-2 max-w-3xl text-sm md:text-[15px] leading-6 text-[#66736d]">{description}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

function ScopeList({ title, items, icon: Icon = CheckCircle2 }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-xl border bg-white p-5" style={{ borderColor: brand.line }}>
      <h3 className="text-sm font-semibold text-[#17211d] mb-3">{title}</h3>
      <ul className="space-y-3">
        {items.map((item, i) => (
          <li key={`${title}-${i}`} className="flex gap-3 text-sm leading-6 text-[#35423c]">
            <Icon className="size-4 text-[#24a35a] mt-1 shrink-0" />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Stat({ icon: Icon, label, value, highlight = false }) {
  return (
    <div className={`rounded-xl border px-4 py-4 ${highlight ? "bg-[#eaf5ee]" : "bg-white"}`} style={{ borderColor: highlight ? "#b9dbc5" : brand.line }}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-[#66736d]">
        <Icon className="size-4 text-[#17613f]" /> {label}
      </div>
      <div className={`mt-2 font-semibold tracking-tight ${highlight ? "text-xl text-[#123c32]" : "text-base text-[#17211d]"}`}>{value}</div>
    </div>
  );
}

function InvestmentTable({ items }) {
  if (!items?.length) return null;
  return (
    <div className="overflow-hidden rounded-xl border bg-white" style={{ borderColor: brand.line }}>
      <div className="grid grid-cols-[1fr_auto] gap-4 bg-[#123c32] px-4 py-3 text-xs font-semibold uppercase tracking-wider text-white">
        <div>Serviço / entrega</div>
        <div>Investimento</div>
      </div>
      <div className="divide-y divide-[#e7ece9]">
        {items.map((item, i) => (
          <div key={`${item.descricao}-${i}`} className="grid grid-cols-[1fr_auto] gap-4 px-4 py-3.5 text-sm">
            <div className="text-[#35423c]">
              <div>{item.descricao}</div>
              {item.unidade === "visita" && Number(item.quantidade || 0) > 0 && <div className="mt-1 text-xs text-[#66736d]">Valor por visita: {brl(item.valor_unitario)}</div>}
            </div>
            <div className="font-semibold text-[#123c32] whitespace-nowrap">{brl(item.valor)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PublicProposal() {
  const { token } = useParams();
  const [proposal, setProposal] = useState(null);
  const [revisionState, setRevisionState] = useState({ superseded: false, current_code: null, replacement_code: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [form, setForm] = useState({ nome: "", cargo: "", email: "" });
  const [requestOpen, setRequestOpen] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [requested, setRequested] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [requestForm, setRequestForm] = useState({ nome: "", email: "", mensagem: "" });

  useEffect(() => {
    let active = true;
    const proposalRequest = fetch(`${API_BASE}/proposals/public/${encodeURIComponent(token)}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.detail || "Proposta não encontrada");
        return r.json();
      });
    const revisionRequest = fetch(`${API_BASE}/proposals/public/${encodeURIComponent(token)}/revision-state`)
      .then((r) => r.ok ? r.json() : { superseded: false })
      .catch(() => ({ superseded: false }));

    Promise.all([proposalRequest, revisionRequest])
      .then(([data, revision]) => {
        if (!active) return;
        setProposal(data);
        setRevisionState(revision || { superseded: false });
        setAccepted(data.status === "aceita");
        setRequested(Boolean(data.needs_revision));
      })
      .catch((e) => active && setError(e.message || "Não foi possível abrir a proposta"))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const superseded = Boolean(revisionState?.superseded);
  const displayCode = revisionState?.current_code || proposal?.codigo || "comercial";
  const canAccept = useMemo(
    () => proposal && !superseded && proposal.status === "enviada" && !proposal.expired && !accepted,
    [proposal, accepted, superseded]
  );
  const canRequestChange = proposal && !superseded && proposal.status === "enviada" && !accepted;

  const scrollToAccept = () => document.getElementById("aceite")?.scrollIntoView({ behavior: "smooth", block: "start" });

  const accept = async () => {
    if (superseded) return setError("Esta versão foi substituída por uma revisão posterior.");
    if (form.nome.trim().length < 2) return setError("Informe o nome da pessoa responsável pelo aceite.");
    setAccepting(true);
    setError("");
    try {
      const r = await fetch(`${API_BASE}/proposals/public/${encodeURIComponent(token)}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Não foi possível registrar o aceite");
      setAccepted(true);
      setRequested(false);
      setProposal((p) => ({ ...p, status: "aceita", accepted_at: data.accepted_at, accepted_by: form.nome, needs_revision: false }));
    } catch (e) {
      setError(e.message || "Falha ao registrar aceite");
    } finally {
      setAccepting(false);
    }
  };

  const requestChange = async () => {
    if (superseded) return setRequestError("Esta versão foi substituída por uma revisão posterior.");
    if (requestForm.nome.trim().length < 2) return setRequestError("Informe seu nome.");
    if (requestForm.mensagem.trim().length < 8) return setRequestError("Descreva brevemente o que gostaria de ajustar.");
    setRequesting(true);
    setRequestError("");
    try {
      const r = await fetch(`${API_BASE}/proposals/public/${encodeURIComponent(token)}/request-change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestForm),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || "Não foi possível registrar a solicitação");
      setRequested(true);
      setRequestOpen(false);
      setProposal((p) => ({ ...p, needs_revision: true, revision_requested_at: data.requested_at }));
    } catch (e) {
      setRequestError(e.message || "Falha ao registrar solicitação");
    } finally {
      setRequesting(false);
    }
  };

  const downloadPdf = () => {
    window.location.href = `${API_BASE}/proposals/public/${encodeURIComponent(token)}/pdf`;
  };

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-[#f4f7f5] text-[#66736d]">
      <div className="flex items-center gap-3"><div className="size-2 rounded-full bg-[#24a35a] animate-pulse" /> Carregando proposta…</div>
    </div>
  );

  if (error && !proposal) return (
    <div className="min-h-screen grid place-items-center bg-[#f4f7f5] p-6 text-[#17211d]">
      <div className="max-w-md w-full rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
        <AlertTriangle className="size-9 text-red-600 mx-auto mb-3" />
        <h1 className="text-xl font-semibold">Não foi possível abrir a proposta</h1>
        <p className="text-sm text-[#66736d] mt-2">{error}</p>
      </div>
    </div>
  );

  const nav = [
    ["visao-geral", "Visão geral"],
    ["escopo", "Escopo"],
    ["investimento", "Investimento"],
    ["condicoes", "Condições"],
    ["aceite", "Aceite"],
  ];

  return (
    <div className="min-h-screen bg-[#f4f7f5] text-[#17211d] pb-24 lg:pb-0">
      <header className="sticky top-0 z-30 border-b border-[#dce5e0] bg-white/95 backdrop-blur">
        <div className="max-w-7xl mx-auto h-16 px-4 md:px-7 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo-demoraes.png" alt="Demoraes Consultoria" className="size-10 object-contain shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold tracking-tight leading-none text-[#008a45]">DEMORAES CONSULTORIA</div>
              <div className="text-[11px] text-[#66736d] mt-1">Engenharia · Segurança e Saúde no Trabalho</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline-flex items-center gap-1.5 text-xs text-[#66736d] mr-2"><ShieldCheck className="size-4 text-[#17613f]" /> Ambiente seguro</span>
            {proposal.pdf_available && (
              <Button variant="outline" size="sm" onClick={downloadPdf} className="border-[#cdd9d2] bg-white text-[#123c32] hover:bg-[#f2f7f4]">
                <Download className="size-4 mr-1.5" /> PDF
              </Button>
            )}
          </div>
        </div>
      </header>

      <section id="visao-geral" className="scroll-mt-28 bg-[#123c32] text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-7 py-10 md:py-16">
          <div className="grid lg:grid-cols-[1fr_350px] gap-8 lg:gap-14 items-end">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[11px] font-semibold tracking-[0.14em] uppercase">Proposta {displayCode}</span>
                {superseded && <span className="rounded-full bg-slate-200/20 px-3 py-1 text-[11px] font-semibold text-slate-100">Versão substituída</span>}
                {accepted && <span className="rounded-full bg-emerald-300/20 px-3 py-1 text-[11px] font-semibold text-emerald-100">Aceita</span>}
                {requested && !accepted && !superseded && <span className="rounded-full bg-amber-300/20 px-3 py-1 text-[11px] font-semibold text-amber-100">Ajuste solicitado</span>}
                {proposal.expired && !accepted && !superseded && <span className="rounded-full bg-red-300/20 px-3 py-1 text-[11px] font-semibold text-red-100">Expirada</span>}
              </div>
              <div className="text-sm text-emerald-100/80">Preparada exclusivamente para</div>
              <h1 className="mt-2 max-w-4xl text-3xl md:text-5xl lg:text-6xl font-semibold tracking-[-0.035em] leading-[1.05]">{proposal.cliente}</h1>
              <p className="mt-6 max-w-3xl text-base md:text-xl leading-relaxed text-emerald-50/80">{proposal.escopo}</p>
            </div>
            <div className="rounded-2xl border border-white/15 bg-white/10 p-5 backdrop-blur-sm">
              <div className="text-xs uppercase tracking-[0.16em] text-emerald-100/70">Investimento total</div>
              <div className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">{brl(proposal.valor_total)}</div>
              {proposal.num_parcelas > 1 && <div className="mt-2 text-sm text-emerald-50/75">{proposal.num_parcelas} parcelas de {brl(proposal.valor_parcela)}</div>}
              <div className="mt-5 pt-4 border-t border-white/15 flex items-center justify-between text-xs text-emerald-50/75">
                <span>{proposal.duracao_meses} meses de duração</span>
                <span>{proposal.expires_at ? `até ${dateBR(proposal.expires_at)}` : `${proposal.validade_dias} dias`}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="sticky top-16 z-20 border-b border-[#dce5e0] bg-[#f4f7f5]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 md:px-7 overflow-x-auto">
          <nav className="h-12 flex items-center gap-5 md:gap-7 min-w-max text-xs font-medium text-[#53615b]">
            {nav.map(([id, label]) => <a key={id} href={`#${id}`} className="hover:text-[#123c32] transition-colors">{label}</a>)}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-5 md:px-7 py-7 md:py-10">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_330px] gap-7 lg:gap-9">
          <div className="space-y-6 min-w-0">
            {superseded && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 md:p-6">
                <div className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="size-5" /> Esta versão foi substituída</div>
                <p className="mt-2 text-sm leading-6 text-amber-900/75">Este link permanece disponível apenas para consulta histórica. {revisionState.replacement_code ? `A versão vigente é ${revisionState.replacement_code}.` : "Existe uma revisão comercial posterior."} Não é possível aceitar nem solicitar alterações nesta versão.</p>
              </div>
            )}

            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <Stat icon={Layers3} label="Duração" value={`${proposal.duracao_meses} meses`} />
              <Stat icon={CalendarDays} label="Validade" value={proposal.expires_at ? dateBR(proposal.expires_at) : `${proposal.validade_dias} dias`} />
              <Stat icon={Landmark} label="Parcelamento" value={proposal.num_parcelas > 1 ? `${proposal.num_parcelas}x` : "À vista"} />
              <Stat icon={BadgeCheck} label="Investimento" value={brl(proposal.valor_total)} highlight />
            </div>

            <Section id="escopo" eyebrow="02 · Escopo" title="O que está contemplado" description="Entregas organizadas por natureza para que o escopo seja simples de conferir e acompanhar.">
              <div className="grid md:grid-cols-2 gap-4">
                <ScopeList title="Programas, laudos e avaliações" items={proposal.programas} />
                <ScopeList title="Gestão e acompanhamento" items={proposal.acompanhamento} icon={Layers3} />
                <ScopeList title="Benefícios e bônus" items={proposal.bonus} icon={Sparkles} />
                <ScopeList title="Também incluídos" items={proposal.inclusos} />
              </div>
            </Section>

            <Section id="investimento" eyebrow="03 · Investimento" title="Condição comercial" description={proposal.exibir_precos_itens ? "Os valores abaixo foram distribuídos entre os itens contratados e reconciliam com o investimento total da proposta." : "A proposta foi estruturada com investimento global para todo o escopo contratado."}>
              {proposal.exibir_precos_itens && <InvestmentTable items={proposal.itens_precificados} />}
              <div className="mt-4 rounded-2xl border border-[#b9dbc5] bg-[#eaf5ee] p-5 md:p-7 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-[#66736d]">Investimento total</div>
                  <div className="mt-1 text-3xl md:text-4xl font-semibold tracking-tight text-[#123c32]">{brl(proposal.valor_total)}</div>
                </div>
                <div className="md:text-right">
                  <div className="text-xs text-[#66736d]">Condição de pagamento</div>
                  <div className="mt-1 font-semibold text-[#17211d]">{proposal.num_parcelas > 1 ? `${proposal.num_parcelas}x de ${brl(proposal.valor_parcela)}` : "Pagamento integral"}</div>
                  <div className="mt-1 text-xs text-[#66736d]">Duração contratual: {proposal.duracao_meses} meses</div>
                </div>
              </div>
            </Section>

            <Section id="condicoes" eyebrow="04 · Condições" title="Prazos, condições e limites" description="Premissas objetivas para manter expectativas alinhadas durante toda a execução.">
              <div className="grid md:grid-cols-2 gap-4">
                <ScopeList title="Prazos" items={proposal.prazos} icon={Clock3} />
                <ScopeList title="Condições comerciais" items={proposal.condicoes} icon={Landmark} />
              </div>
              {!!proposal.nao_inclusos?.length && (
                <div className="mt-4 rounded-xl border border-[#dce5e0] bg-[#f7faf8] p-5">
                  <h3 className="text-sm font-semibold text-[#35423c]">Premissas e limites de escopo</h3>
                  <p className="mt-1 text-xs text-[#66736d]">Itens não contemplados, salvo contratação expressa posterior.</p>
                  <ul className="mt-4 grid md:grid-cols-2 gap-x-5 gap-y-2.5 text-sm text-[#66736d]">
                    {proposal.nao_inclusos.map((item, i) => <li key={`limite-${i}`} className="flex gap-2"><span className="text-[#9aaa9f]">•</span><span>{item}</span></li>)}
                  </ul>
                </div>
              )}
              {proposal.observacoes && <div className="mt-4 rounded-xl border border-[#dce5e0] bg-white p-5 text-sm leading-6 text-[#53615b] whitespace-pre-wrap"><span className="font-semibold text-[#35423c]">Observações: </span>{proposal.observacoes}</div>}
            </Section>

            <Section id="demoraes" eyebrow="05 · Confiança" title="Por que a Demoraes" description="Uma proposta comercial deve ser clara sobre valor e também sobre quem responde tecnicamente pela execução." muted>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  ["Engenharia especializada", "Escopo estruturado por profissionais de SST com foco em aplicação prática e rastreabilidade."],
                  ["Conformidade", "Entregas organizadas para facilitar gestão, auditorias e atendimento a requisitos aplicáveis."],
                  ["Gestão integrada", "Programas, avaliações, treinamentos e acompanhamento tratados como partes do mesmo sistema de gestão."],
                  ["Comunicação objetiva", "Prazos, dependências, inclusões e limites de escopo registrados de forma transparente."],
                ].map(([title, desc]) => (
                  <div key={title} className="rounded-xl border border-[#dce5e0] bg-white p-5">
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#123c32]"><ShieldCheck className="size-4" /> {title}</div>
                    <p className="mt-2 text-sm leading-6 text-[#66736d]">{desc}</p>
                  </div>
                ))}
              </div>
            </Section>

            <Section id="aceite" eyebrow="06 · Decisão" title={superseded ? "Versão substituída" : accepted ? "Proposta aceita" : "Próximo passo"} description={superseded ? "Esta versão permanece disponível somente para consulta histórica." : accepted ? "O aceite desta proposta foi registrado no sistema." : "Você pode aceitar esta versão ou solicitar um ajuste comercial sem precisar responder por outro canal."}>
              {superseded ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                  <div className="flex items-center gap-2 font-semibold text-amber-800"><HistoryIcon /> Versão encerrada por revisão</div>
                  <p className="mt-2 text-sm text-amber-800/75">{revisionState.replacement_code ? `Considere a versão ${revisionState.replacement_code} para qualquer decisão comercial.` : "Solicite à Demoraes a revisão vigente para prosseguir."}</p>
                </div>
              ) : proposal.expired && !accepted ? (
                <div className="rounded-xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-center gap-2 font-semibold text-red-700"><AlertTriangle className="size-5" /> Proposta expirada</div>
                  <p className="mt-2 text-sm text-red-700/75">Solicite uma atualização comercial para receber uma nova versão.</p>
                </div>
              ) : accepted ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5 md:p-6">
                  <div className="flex items-center gap-2 font-semibold text-emerald-800"><FileCheck2 className="size-5" /> Aceite registrado com sucesso</div>
                  <p className="mt-2 text-sm text-emerald-800/75">Registrado em {dateBR(proposal.accepted_at)}{proposal.accepted_by ? ` por ${proposal.accepted_by}` : ""}.</p>
                </div>
              ) : canAccept ? (
                <div className="grid md:grid-cols-3 gap-4 max-w-4xl">
                  <div><Label className="text-[#35423c]">Nome *</Label><Input className="mt-1 bg-white text-[#17211d] border-[#cdd9d2]" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
                  <div><Label className="text-[#35423c]">Cargo</Label><Input className="mt-1 bg-white text-[#17211d] border-[#cdd9d2]" value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} /></div>
                  <div><Label className="text-[#35423c]">E-mail</Label><Input className="mt-1 bg-white text-[#17211d] border-[#cdd9d2]" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                  {error && <p className="md:col-span-3 text-sm text-red-600">{error}</p>}
                  <div className="md:col-span-3 flex flex-col sm:flex-row sm:items-center gap-3">
                    <Button onClick={accept} disabled={accepting} className="bg-[#123c32] text-white hover:bg-[#17613f] h-11 px-6">{accepting ? "Registrando…" : "Aceitar esta proposta"}<ArrowRight className="size-4 ml-2" /></Button>
                    <p className="text-[11px] leading-5 text-[#718079] max-w-xl">O aceite registra data/hora e evidências técnicas da transação. A formalização contratual e assinaturas exigidas continuam seguindo o processo contratual aplicável.</p>
                  </div>
                </div>
              ) : !proposal.expired ? (
                <p className="text-sm text-[#66736d]">Esta proposta não está disponível para aceite neste momento.</p>
              ) : null}

              {canRequestChange && (
                <div className="mt-5 border-t border-[#e3e9e5] pt-5 max-w-4xl">
                  {requested ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-5">
                      <div className="flex items-center gap-2 font-semibold text-amber-800"><MessageSquareText className="size-5" /> Solicitação de ajuste registrada</div>
                      <p className="mt-2 text-sm text-amber-800/75">A equipe comercial recebeu sua solicitação. Esta proposta continua disponível para consulta enquanto a nova versão é preparada.</p>
                    </div>
                  ) : requestOpen ? (
                    <div className="rounded-xl border border-[#dce5e0] bg-[#f7faf8] p-5">
                      <div className="flex items-center gap-2 font-semibold text-[#123c32]"><MessageSquareText className="size-5" /> Solicitar ajuste</div>
                      <p className="mt-1 text-sm text-[#66736d]">Informe o ponto que deseja revisar em escopo, prazo, condição ou investimento.</p>
                      <div className="mt-4 grid md:grid-cols-2 gap-4">
                        <div><Label className="text-[#35423c]">Nome *</Label><Input className="mt-1 bg-white border-[#cdd9d2]" value={requestForm.nome} onChange={(e) => setRequestForm({ ...requestForm, nome: e.target.value })} /></div>
                        <div><Label className="text-[#35423c]">E-mail</Label><Input type="email" className="mt-1 bg-white border-[#cdd9d2]" value={requestForm.email} onChange={(e) => setRequestForm({ ...requestForm, email: e.target.value })} /></div>
                        <div className="md:col-span-2">
                          <Label className="text-[#35423c]">O que gostaria de ajustar? *</Label>
                          <textarea rows={4} maxLength={2000} className="mt-1 w-full rounded-md border border-[#cdd9d2] bg-white px-3 py-2 text-sm text-[#17211d] outline-none focus:ring-2 focus:ring-[#17613f]/30" value={requestForm.mensagem} onChange={(e) => setRequestForm({ ...requestForm, mensagem: e.target.value })} placeholder="Ex.: revisar prazo de execução e condição de pagamento" />
                        </div>
                        {requestError && <p className="md:col-span-2 text-sm text-red-600">{requestError}</p>}
                        <div className="md:col-span-2 flex flex-wrap gap-2">
                          <Button onClick={requestChange} disabled={requesting} className="bg-[#123c32] text-white hover:bg-[#17613f]">{requesting ? "Enviando…" : "Enviar solicitação"}</Button>
                          <Button variant="outline" onClick={() => { setRequestOpen(false); setRequestError(""); }} className="border-[#cdd9d2]">Cancelar</Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <Button variant="outline" onClick={() => setRequestOpen(true)} className="border-[#cdd9d2] text-[#123c32] hover:bg-[#f2f7f4]"><MessageSquareText className="size-4 mr-2" /> Solicitar ajuste nesta proposta</Button>
                  )}
                </div>
              )}
            </Section>
          </div>

          <aside className="hidden lg:block">
            <div className="sticky top-32 space-y-4">
              <div className="rounded-2xl border border-[#cdd9d2] bg-white p-6 shadow-[0_18px_50px_rgba(18,60,50,0.08)]">
                <div className="text-[11px] uppercase tracking-[0.15em] text-[#66736d]">Investimento</div>
                <div className="mt-2 text-3xl font-semibold tracking-tight text-[#123c32]">{brl(proposal.valor_total)}</div>
                {proposal.num_parcelas > 1 && <div className="mt-1 text-sm text-[#66736d]">{proposal.num_parcelas}x de {brl(proposal.valor_parcela)}</div>}
                <div className="mt-5 pt-4 border-t border-[#e3e9e5] space-y-2 text-xs text-[#66736d]">
                  <div className="flex justify-between gap-4"><span>Duração</span><strong className="text-[#35423c]">{proposal.duracao_meses} meses</strong></div>
                  <div className="flex justify-between gap-4"><span>Validade</span><strong className="text-[#35423c]">{proposal.expires_at ? dateBR(proposal.expires_at) : `${proposal.validade_dias} dias`}</strong></div>
                </div>
                {superseded && <div className="mt-5 rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-800 flex items-center gap-2"><AlertTriangle className="size-4" /> Versão substituída</div>}
                {canAccept && <Button onClick={scrollToAccept} className="mt-5 w-full bg-[#123c32] text-white hover:bg-[#17613f]">Aceitar proposta <ArrowRight className="size-4 ml-2" /></Button>}
                {accepted && !superseded && <div className="mt-5 rounded-lg bg-emerald-50 p-3 text-xs font-medium text-emerald-800 flex items-center gap-2"><FileCheck2 className="size-4" /> Proposta aceita</div>}
                {requested && !accepted && !superseded && <div className="mt-3 rounded-lg bg-amber-50 p-3 text-xs font-medium text-amber-800 flex items-center gap-2"><MessageSquareText className="size-4" /> Ajuste solicitado</div>}
                {canRequestChange && !requested && <Button variant="outline" onClick={() => { setRequestOpen(true); scrollToAccept(); }} className="mt-2 w-full border-[#cdd9d2] text-[#123c32] hover:bg-[#f2f7f4]"><MessageSquareText className="size-4 mr-2" /> Solicitar ajuste</Button>}
                {proposal.pdf_available && <Button variant="outline" onClick={downloadPdf} className="mt-2 w-full border-[#cdd9d2] text-[#123c32] hover:bg-[#f2f7f4]"><Download className="size-4 mr-2" /> Baixar PDF</Button>}
              </div>
              <div className="rounded-xl border border-[#dce5e0] bg-[#f7faf8] p-4 text-xs leading-5 text-[#66736d]">
                <div className="font-semibold text-[#35423c] flex items-center gap-2"><ShieldCheck className="size-4 text-[#17613f]" /> Proposta digital segura</div>
                <p className="mt-1.5">Este link é específico desta versão. Alterações comerciais invalidam o link anterior.</p>
              </div>
            </div>
          </aside>
        </div>
      </main>

      <footer className="border-t border-[#dce5e0] bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-7 py-7 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-[#718079]">
          <div><span className="font-semibold text-[#35423c]">Demoraes Consultoria</span> · Engenharia & Segurança do Trabalho</div>
          <div className="flex items-center gap-2"><ShieldCheck className="size-4 text-[#17613f]" /> Proposta {displayCode}</div>
        </div>
      </footer>

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[#cdd9d2] bg-white/98 backdrop-blur px-4 py-3 safe-area-inset-bottom shadow-[0_-10px_30px_rgba(18,60,50,0.10)]">
        <div className="max-w-xl mx-auto flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-[#66736d]">Investimento</div>
            <div className="font-semibold text-[#123c32] truncate">{brl(proposal.valor_total)}</div>
          </div>
          {canAccept ? (
            <Button onClick={scrollToAccept} className="shrink-0 bg-[#123c32] text-white hover:bg-[#17613f]">Aceitar <ArrowRight className="size-4 ml-1" /></Button>
          ) : canRequestChange && !requested ? (
            <Button variant="outline" onClick={() => { setRequestOpen(true); scrollToAccept(); }} className="shrink-0 border-[#cdd9d2] text-[#123c32]"><MessageSquareText className="size-4 mr-1" /> Ajuste</Button>
          ) : proposal.pdf_available ? (
            <Button variant="outline" onClick={downloadPdf} className="shrink-0 border-[#cdd9d2] text-[#123c32]"><Download className="size-4 mr-1" /> PDF</Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function HistoryIcon() {
  return <span className="grid size-5 place-items-center rounded-full border border-current text-[10px] font-bold">R</span>;
}
